import { supabase, getUserRole } from '../config/supabase.js'
import { showSuccess, showError } from '../utils/toast.js'
import { navigateToPasswordSetup, navigateToList } from '../router/router.js'
import './user-profile.css'

export function showUserProfile() {
  document.getElementById('content').classList.add('hidden')
  document.getElementById('person-detail-page').classList.add('hidden')
  document.getElementById('person-edit-page').classList.add('hidden')
  document.getElementById('person-add-page').classList.add('hidden')
  document.getElementById('password-setup-page').classList.add('hidden')
  document.getElementById('user-profile-page').classList.remove('hidden')
}

export async function loadUserProfile() {
  const container = document.getElementById('user-profile-page')
  container.innerHTML = '<div class="loading">Loading profile...</div>'
  
  try {
    // Get current user
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) {
      showError('Please log in to view profile')
      navigateToList()
      return
    }

    // Get user profile
    const { data: profile, error } = await supabase
      .from('user_profile')
      .select('*')
      .eq('id', user.id)
      .single()

    if (error) throw error

    renderProfile(user, profile)
    
  } catch (error) {
    console.error('Error loading profile:', error)
    container.innerHTML = `
      <div class="error">
        <strong>Error loading profile:</strong> ${error.message}
      </div>
    `
  }
}

function renderProfile(user, profile) {
  const container = document.getElementById('user-profile-page')
  
  // Determine login method
  let loginMethod = 'Magic Link'
  if (profile.has_password && profile.prefer_magic_link) {
    loginMethod = 'Both (Password & Magic Link)'
  } else if (profile.has_password) {
    loginMethod = 'Password'
  }

  // Role display
  const roleDisplay = profile.role.charAt(0).toUpperCase() + profile.role.slice(1)
  const roleColor = 
    profile.role === 'admin' ? '#f44336' :
    profile.role === 'family' ? '#4CAF50' :
    '#2196F3'

  container.innerHTML = `
    <div class="profile-container">
      <button id="back-from-profile" class="back-btn">← Back</button>
      
      <div class="profile-header">
        <div class="profile-avatar">
          ${getInitials(profile.display_name || user.email)}
        </div>
        <div class="profile-header-info">
          <h1>${profile.display_name || user.email}</h1>
          <div class="profile-role" style="background: ${roleColor};">
            ${roleDisplay}
          </div>
        </div>
      </div>

      <div class="profile-sections">
        <!-- Account Information -->
        <div class="profile-section">
          <h2>Account Information</h2>
          
          <div class="info-row">
            <div class="info-label">Email</div>
            <div class="info-value">${user.email}</div>
          </div>

          <div class="info-row">
            <div class="info-label">Display Name</div>
            <div class="info-value">
              <span id="display-name-text">${profile.display_name || 'Not set'}</span>
              <button class="btn-edit" id="edit-display-name-btn">Edit</button>
            </div>
          </div>

          <div id="display-name-form" class="edit-form hidden">
            <input 
              type="text" 
              id="display-name-input" 
              placeholder="Enter your name"
              value="${profile.display_name || ''}"
              maxlength="100"
            >
            <div class="edit-form-actions">
              <button class="btn-text" id="cancel-display-name">Cancel</button>
              <button class="btn-primary-small" id="save-display-name">Save</button>
            </div>
          </div>

          <div class="info-row">
            <div class="info-label">Role</div>
            <div class="info-value">${roleDisplay}</div>
          </div>

          <div class="info-row">
            <div class="info-label">Member Since</div>
            <div class="info-value">${formatDate(profile.created_at)}</div>
          </div>
        </div>

        <!-- Security Settings -->
        <div class="profile-section">
          <h2>Security & Login</h2>
          
          <div class="info-row">
            <div class="info-label">Login Method</div>
            <div class="info-value">
              <span class="login-method-badge">${loginMethod}</span>
            </div>
          </div>

          ${profile.role !== 'admin' ? `
            <div class="security-actions">
              ${!profile.has_password ? `
                <div class="security-recommendation">
                  <div class="recommendation-icon">💡</div>
                  <div class="recommendation-text">
                    <strong>Recommended:</strong> Set a password for faster future logins
                  </div>
                </div>
              ` : ''}
              
              <button class="btn-secondary-full" id="manage-password-btn">
                ${profile.has_password ? 'Change Password' : 'Set Password'}
              </button>
            </div>
          ` : `
            <div class="security-actions">
              <button class="btn-secondary-full" id="manage-password-btn">
                Change Password
              </button>
            </div>
          `}
        </div>

        <!-- Access Information -->
        <div class="profile-section">
          <h2>Access Level</h2>
          
          <div class="access-description">
            ${getAccessDescription(profile.role)}
          </div>
        </div>
      </div>
    </div>
  `

  setupProfileListeners(user.id, profile)
}

function setupProfileListeners(userId, profile) {
  // Back button
  document.getElementById('back-from-profile').addEventListener('click', () => {
    navigateToList()
  })

  // Edit display name
  const editBtn = document.getElementById('edit-display-name-btn')
  const displayNameText = document.getElementById('display-name-text')
  const displayNameForm = document.getElementById('display-name-form')
  const displayNameInput = document.getElementById('display-name-input')
  const cancelBtn = document.getElementById('cancel-display-name')
  const saveBtn = document.getElementById('save-display-name')

  editBtn.addEventListener('click', () => {
    displayNameText.classList.add('hidden')
    editBtn.classList.add('hidden')
    displayNameForm.classList.remove('hidden')
    displayNameInput.focus()
  })

  cancelBtn.addEventListener('click', () => {
    displayNameForm.classList.add('hidden')
    displayNameText.classList.remove('hidden')
    editBtn.classList.remove('hidden')
    displayNameInput.value = profile.display_name || ''
  })

  saveBtn.addEventListener('click', async () => {
    const newName = displayNameInput.value.trim()
    
    saveBtn.disabled = true
    saveBtn.textContent = 'Saving...'

    try {
      const { error } = await supabase
        .from('user_profile')
        .update({ display_name: newName || null })
        .eq('id', userId)

      if (error) throw error

      // Update UI
      displayNameText.textContent = newName || 'Not set'
      profile.display_name = newName
      
      // Update header if it exists
      const headerName = document.querySelector('.profile-header h1')
      if (headerName) {
        headerName.textContent = newName || profile.email
      }

      // Update header user info
      const userEmailElement = document.getElementById('user-email')
      if (userEmailElement && newName) {
        userEmailElement.textContent = newName
      }

      displayNameForm.classList.add('hidden')
      displayNameText.classList.remove('hidden')
      editBtn.classList.remove('hidden')

      showSuccess('Display name updated!')

    } catch (error) {
      console.error('Error updating display name:', error)
      showError('Failed to update display name')
    } finally {
      saveBtn.disabled = false
      saveBtn.textContent = 'Save'
    }
  })

  // Manage password button
  const managePasswordBtn = document.getElementById('manage-password-btn')
  managePasswordBtn.addEventListener('click', () => {
    navigateToPasswordSetup()
  })
}

function getInitials(name) {
  if (!name) return '?'
  
  const parts = name.split(/[\s@]+/)
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase()
  }
  return name.substring(0, 2).toUpperCase()
}

function formatDate(dateString) {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  })
}

function getAccessDescription(role) {
  switch(role) {
    case 'admin':
      return `
        <ul class="access-list">
          <li>✓ View all family members (including living)</li>
          <li>✓ Edit any person's information</li>
          <li>✓ Add new family members</li>
          <li>✓ Delete records</li>
          <li>✓ Manage user permissions</li>
        </ul>
      `
    case 'family':
      return `
        <ul class="access-list">
          <li>✓ View all family members (including living)</li>
          <li>✓ See full birth dates</li>
          <li>✓ View private family notes</li>
          <li>✗ Cannot edit or add people</li>
        </ul>
      `
    default:
      return `
        <ul class="access-list">
          <li>✓ View deceased family members</li>
          <li>✓ Browse family tree</li>
          <li>✗ Cannot see full birth dates of living people</li>
          <li>✗ Cannot edit or add people</li>
        </ul>
      `
  }
}
